import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Search, Upload } from 'lucide-react';
import { customerAPI, parseFileData } from '../utils/api';
import CustomerModal from '../components/CustomerModal';
import '../styles/Customers.css';

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchQuery, customers]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getAll();
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      showAlert('Error fetching customers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    try {
      const response = await customerAPI.search(searchQuery);
      setFilteredCustomers(response.data);
    } catch (error) {
      showAlert('Error searching customers', 'error');
    }
  };

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await customerAPI.delete(id);
        showAlert('Customer deleted successfully', 'success');
        fetchCustomers();
      } catch (error) {
        showAlert('Error deleting customer', 'error');
      }
    }
  };

  const handleSaveCustomer = async (formData) => {
    try {
      if (editingCustomer) {
        await customerAPI.update(editingCustomer.id, formData);
        showAlert('Customer updated successfully', 'success');
      } else {
        await customerAPI.create(formData);
        showAlert('Customer created successfully', 'success');
      }
      fetchCustomers();
      setShowModal(false);
    } catch (error) {
      showAlert('Error saving customer', 'error');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const customers = await parseFileData(file);
      await customerAPI.bulkCreate(customers);
      showAlert(`Successfully imported ${customers.length} customers`, 'success');
      fetchCustomers();
    } catch (error) {
      showAlert('Error importing customers: ' + error.message, 'error');
    } finally {
      setLoading(false);
      event.target.value = ''; // Reset file input
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Customer Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => fileInputRef.current.click()}>
            <Upload size={18} />
            Import CSV/Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="btn btn-primary" onClick={handleAddCustomer}>
            <Plus size={18} />
            Add Customer
          </button>
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}

      <div className="card">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, phone, GSTIN, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <p>No customers found</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>GSTIN</th>
                  <th>City</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td>{customer.fullName}</td>
                    <td>{customer.companyName || '-'}</td>
                    <td>{customer.phoneNumber}</td>
                    <td>{customer.emailId || '-'}</td>
                    <td>{customer.gstinNumber || '-'}</td>
                    <td>{customer.city}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => handleEditCustomer(customer)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDeleteCustomer(customer.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <CustomerModal
          customer={editingCustomer}
          onSave={handleSaveCustomer}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default Customers;
